import React, { useCallback, useEffect, useMemo, useState } from "react";
import signupimage from "../../images/signup-g.svg";
import { Link, Navigate } from "react-router-dom";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  fullName: "",
  phone: "",
  email: "",
  password: "",
};

const MyAccountSignUp = () => {
  const { role, session, isSupabaseConfigured } = useAuth();
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [adminExists, setAdminExists] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [cashiers, setCashiers] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const canManageCashiers = role === "admin";
  const canBootstrapAdmin = !adminExists;

  const pageMode = useMemo(() => {
    if (canBootstrapAdmin) {
      return "bootstrap-admin";
    }
    if (canManageCashiers) {
      return "cashier";
    }
    return "blocked";
  }, [canBootstrapAdmin, canManageCashiers]);

  const loadProfiles = useCallback(async () => {
    if (!isSupabaseConfigured || !supabase) {
      setLoaderStatus(false);
      return;
    }

    const baseQuery = supabase.from("profiles");
    const { data, error: profileError } = session
      ? await baseQuery
          .select("id, full_name, role, phone")
          .in("role", ["admin", "cashier"])
      : await baseQuery
          .select("id, role")
          .in("role", ["admin", "cashier"]);

    if (profileError) {
      setLoaderStatus(false);
      return;
    }

    const allProfiles = data || [];
    setAdminExists(allProfiles.some((item) => item.role === "admin"));
    setCashiers(allProfiles.filter((item) => item.role === "cashier"));
    setLoaderStatus(false);
  }, [isSupabaseConfigured, session]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setMessage("");
    setError("");
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");

    if (!isSupabaseConfigured || !supabase) {
      const nextRole = canBootstrapAdmin ? "admin" : "cashier";
      const mockUser = {
        id: "local-dev-admin",
        email: form.email || "admin@almoune.local",
      };
      const mockProfile = {
        id: "local-dev-admin",
        full_name: form.fullName || "Admin Local",
        role: nextRole,
        phone: form.phone || "",
        is_active: true,
      };
      localStorage.setItem(
        "almoune_local_dev_user",
        JSON.stringify({ user: mockUser, profile: mockProfile })
      );
      setMessage("Compte local cree ! Redirection...");
      setTimeout(() => window.location.reload(), 1000);
      setSaving(false);
      return;
    }

    const nextRole = canBootstrapAdmin ? "admin" : "cashier";
    if (!canBootstrapAdmin && !canManageCashiers) {
      setError("Seul un administrateur peut creer un cashier.");
      setSaving(false);
      return;
    }

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: {
        data: {
          full_name: form.fullName.trim(),
          role: nextRole,
        },
      },
    });

    if (authError || !authData.user) {
      setError(authError?.message || "Creation du compte impossible.");
      setSaving(false);
      return;
    }

    const { error: profileError } = await supabase.from("profiles").upsert(
      [
        {
          id: authData.user.id,
          full_name: form.fullName.trim(),
          phone: form.phone.trim(),
          role: nextRole,
          is_active: true,
        },
      ],
      { onConflict: "id" }
    );

    if (profileError) {
      setError(profileError.message);
      setSaving(false);
      return;
    }

    setMessage(
      nextRole === "admin"
        ? "Premier administrateur cree. Vous pouvez maintenant vous connecter."
        : "Compte cashier cree avec succes."
    );
    resetForm();
    await loadProfiles();
    setSaving(false);
  };

  if (pageMode === "blocked" && session && role !== "admin") {
    return <Navigate to="/Pilotage" replace />;
  }

  return (
    <div>
      <ScrollToTop />
      <section className="my-lg-14 my-8">
        <div className="container">
          <div className="row">
            {canManageCashiers ? <AdminSidebar activePath="/MyAccountSignUp" /> : null}

            <div className={canManageCashiers ? "col-lg-9 col-md-8 col-12" : "col-12"}>
              {loaderStatus ? (
                <div className="alert alert-light border mb-0">Chargement des comptes...</div>
              ) : (
                <div className="p-6 p-lg-10">
                  <div className="row justify-content-center align-items-center g-5">
                    <div className="col-12 col-md-6 col-lg-4">
                      <img src={signupimage} alt="Gestion des comptes" className="img-fluid" />
                    </div>
                    <div className="col-12 col-md-6 col-lg-7">
                      <div className="mb-5">
                        <h1 className="mb-1 h2 fw-bold">
                          {canBootstrapAdmin
                            ? "Creation du premier administrateur"
                            : "Creation d'un cashier"}
                        </h1>
                        <p className="mb-0">
                          {canBootstrapAdmin
                            ? "Cette etape initialise l'administration Almoune dans Supabase."
                            : "Un administrateur peut creer des comptes cashier pour la vente et la gestion quotidienne."}
                        </p>
                      </div>

                      <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              name="fullName"
                              placeholder="Nom complet"
                              value={form.fullName}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              name="phone"
                              placeholder="Telephone"
                              value={form.phone}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-12">
                            <input
                              type="email"
                              className="form-control"
                              name="email"
                              placeholder="Email"
                              value={form.email}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-12">
                            <input
                              type="password"
                              className="form-control"
                              name="password"
                              placeholder="Mot de passe"
                              value={form.password}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          {message ? (
                            <div className="col-12">
                              <div className="alert alert-success mb-0">{message}</div>
                            </div>
                          ) : null}
                          {error ? (
                            <div className="col-12">
                              <div className="alert alert-danger mb-0">{error}</div>
                            </div>
                          ) : null}
                          <div className="col-12 d-grid">
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                              {saving
                                ? "Enregistrement..."
                                : canBootstrapAdmin
                                ? "Creer l'administrateur"
                                : "Creer le cashier"}
                            </button>
                          </div>
                          {!canManageCashiers ? (
                            <div>
                              Deja un compte ? <Link to="/MyAccountSignIn">Connexion</Link>
                            </div>
                          ) : null}
                        </div>
                      </form>

                      {canManageCashiers ? (
                        <div className="card border-0 shadow-sm mt-6">
                          <div className="card-body p-4">
                            <h5 className="mb-3">Cashiers actifs</h5>
                            {cashiers.length ? (
                              <ul className="list-group list-group-flush">
                                {cashiers.map((cashier) => (
                                  <li className="list-group-item px-0" key={cashier.id}>
                                    <div className="fw-semibold">{cashier.full_name || "Cashier"}</div>
                                    <small className="text-muted">
                                      {cashier.phone || "Telephone non renseigne"}
                                    </small>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-muted mb-0">Aucun cashier cree pour le moment.</p>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAccountSignUp;
